import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Users, UserPlus, Crown, Shield, User, 
  Mail, Trash2, LogOut, Check, X, Edit2, Save,
  Building2, Plus, Loader2, CreditCard, Sparkles, Rocket, ExternalLink,
  Clock, CheckCircle2, XCircle, Send
} from 'lucide-react'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { useUser } from '../hooks/useUser'
import { useToast } from '../hooks/useToast'
import UpgradeModal from '../components/UpgradeModal'

interface AccountMember {
  user_id: string
  email: string
  name: string
  role: 'owner' | 'admin' | 'member'
  joined_at: string
}

interface Account {
  id: string
  name: string
  owner_email: string
  created_at: string
  members: AccountMember[]
  member_count: number
}

interface Invite {
  id: string
  account_id: string
  account_name: string
  inviter_email: string
  invitee_email: string
  role: 'admin' | 'member'
  workspace_ids?: string[]
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  created_at: string
  expires_at: string
  accepted_at?: string
}

interface Workspace {
  id: string
  name: string
}

interface AccountData {
  account: Account | null
  is_owner: boolean
  my_role: string
  is_solo: boolean
  message?: string
}

interface CustomDeadline {
  data: string
  descrizione: string
  importo_stimato?: number
  tipo: string
}

interface FiscalData {
  ragione_sociale?: string
  partita_iva?: string
  codice_fiscale?: string
  regime_fiscale?: 'ordinario' | 'semplificato' | 'forfettario' | 'minimi'
  tipo_attivita?: 'ditta_individuale' | 'srl' | 'srls' | 'snc' | 'sas' | 'spa' | 'professionista' | 'altro'
  periodicita_iva?: 'mensile' | 'trimestrale'
  aliquota_iva_principale?: number
  cassa_previdenza?: 'inps_gestione_separata' | 'inps_commercianti' | 'inps_artigiani' | 'cassa_professionale' | 'nessuna'
  percentuale_contributi?: number
  soggetto_ritenuta?: boolean
  percentuale_ritenuta?: number
  ha_dipendenti?: boolean
  numero_dipendenti?: number
  scadenze_custom?: CustomDeadline[]
  note?: string
  updated_at?: string
}

export default function AccountPage() {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const { user, refreshUser } = useUser()
  const { showToast, confirm } = useToast()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [loadingPortal, setLoadingPortal] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member')
  const [inviteWorkspaceIds, setInviteWorkspaceIds] = useState<string[]>([])
  const [showSentInvites, setShowSentInvites] = useState(false)
  
  // Fiscal data states
  const [fiscalData, setFiscalData] = useState<FiscalData>({})
  const [fiscalSaved, setFiscalSaved] = useState(false)
  const [newDeadline, setNewDeadline] = useState<CustomDeadline>({ data: '', descrizione: '', tipo: 'altro' })
  const [showAddDeadline, setShowAddDeadline] = useState(false)
  const currentPlan = user?.plan || 'trial'
  const isTrialUser = Boolean(user?.is_trial)
  const currentPlanLabel = currentPlan === 'business'
    ? 'Business'
    : currentPlan === 'starter'
      ? 'Starter'
      : currentPlan === 'pro'
        ? 'Pro'
        : 'Trial'

  // Fetch account data
  const { data: accountData, isLoading } = useQuery<AccountData>({
    queryKey: ['account'],
    queryFn: async () => {
      const response = await api.get('/account')
      return response.data
    }
  })

  // Fetch pending invites (invites I received)
  const { data: invitesData } = useQuery<{ invites: Invite[] }>({
    queryKey: ['invites'],
    queryFn: async () => {
      const response = await api.get('/account/invites')
      return response.data
    }
  })

  // Fetch sent invites (invites I sent)
  const { data: sentInvitesData } = useQuery<{ invites: Invite[] }>({
    queryKey: ['sent-invites'],
    queryFn: async () => {
      const response = await api.get('/account/invites/sent')
      return response.data
    },
    enabled: !!accountData?.account && (accountData?.is_owner || accountData?.my_role === 'admin')
  })

  // Fetch workspaces for invite selection
  const { data: workspacesData } = useQuery<{ workspaces: Workspace[] }>({
    queryKey: ['workspaces'],
    queryFn: async () => {
      const response = await api.get('/workspaces')
      return response.data
    },
    enabled: !!accountData?.account
  })

  const invites = invitesData?.invites || []

  // Fetch fiscal data
  const { data: fiscalDataFromServer, isLoading: fiscalLoading } = useQuery<FiscalData | null>({
    queryKey: ['fiscal-data'],
    queryFn: async () => {
      try {
        const response = await api.get('/workspace/fiscal-data')
        return response.data.fiscal_data || null
      } catch {
        return null
      }
    }
  })

  // Update local state when server data loads
  useEffect(() => {
    if (fiscalDataFromServer) {
      setFiscalData(fiscalDataFromServer)
    }
  }, [fiscalDataFromServer])

  // Save fiscal data mutation
  const saveFiscalDataMutation = useMutation({
    mutationFn: async (data: FiscalData) => {
      const response = await api.put('/workspace/fiscal-data', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fiscal-data'] })
      setFiscalSaved(true)
      setTimeout(() => setFiscalSaved(false), 3000)
    }
  })

  const handleFiscalChange = (field: keyof FiscalData, value: FiscalData[keyof FiscalData]) => {
    setFiscalData(prev => ({ ...prev, [field]: value }))
  }

  const addCustomDeadline = () => {
    if (newDeadline.data && newDeadline.descrizione) {
      const updated = [...(fiscalData.scadenze_custom || []), newDeadline]
      setFiscalData(prev => ({ ...prev, scadenze_custom: updated }))
      setNewDeadline({ data: '', descrizione: '', tipo: 'altro' })
      setShowAddDeadline(false)
    }
  }

  const removeCustomDeadline = (index: number) => {
    const updated = (fiscalData.scadenze_custom || []).filter((_, i) => i !== index)
    setFiscalData(prev => ({ ...prev, scadenze_custom: updated }))
  }

  // Create team mutation
  const createTeam = useMutation({
    mutationFn: async (name: string) => {
      const response = await api.post('/account', { name })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
      setShowCreateForm(false)
      setNewTeamName('')
    }
  })

  // Update team name mutation
  const updateTeamName = useMutation({
    mutationFn: async (name: string) => {
      const response = await api.put('/account', { name })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
      setEditingName(false)
    }
  })

  // Invite user mutation
  const inviteUser = useMutation({
    mutationFn: async ({ email, role, workspace_ids }: { email: string; role: 'admin' | 'member'; workspace_ids?: string[] }) => {
      const response = await api.post('/account/invite', { email, role, workspace_ids })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
      queryClient.invalidateQueries({ queryKey: ['sent-invites'] })
      setShowInviteForm(false)
      setInviteEmail('')
      setInviteWorkspaceIds([])
    }
  })

  // Accept invite mutation
  const acceptInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const response = await api.post('/account/invite/accept', { invite_id: inviteId })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
      queryClient.invalidateQueries({ queryKey: ['invites'] })
    }
  })

  // Decline invite mutation
  const declineInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const response = await api.post('/account/invite/decline', { invite_id: inviteId })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] })
    }
  })

  // Revoke invite mutation (for owner/admin to cancel sent invites)
  const revokeInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const response = await api.post(`/account/invite/${inviteId}/revoke`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sent-invites'] })
    }
  })

  // Remove member mutation
  const removeMember = useMutation({
    mutationFn: async (email: string) => {
      const response = await api.delete(`/account/members?email=${encodeURIComponent(email)}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
    }
  })

  // Update member role mutation
  const updateMemberRole = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: 'admin' | 'member' }) => {
      const response = await api.put('/account/members', { email, role })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
    }
  })

  // Leave account mutation
  const leaveAccount = useMutation({
    mutationFn: async () => {
      const response = await api.post('/account/leave')
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
    }
  })

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-amber-500" />
      case 'admin': return <Shield className="w-4 h-4 text-blue-500" />
      default: return <User className="w-4 h-4 text-gray-400" />
    }
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-amber-100 text-amber-700'
      case 'admin': return 'bg-blue-100 text-blue-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl pt-4">
      {/* Header */}
      <div className="space-y-1">
        {/* Page title now in top bar */}
        <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">{t.account.settings}</p>
      </div>

      {/* Piano e Abbonamento Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              user?.plan === 'business' ? 'bg-purple-100 dark:bg-purple-900/50' :
              user?.plan === 'pro' ? 'bg-amber-100 dark:bg-amber-900/50' :
              user?.plan === 'starter' ? 'bg-blue-100 dark:bg-blue-900/50' :
              'bg-gray-100 dark:bg-gray-900/50'
            }`}>
              {user?.plan === 'business' ? <Crown className="w-5 h-5 text-purple-600 dark:text-purple-400" /> :
               user?.plan === 'pro' ? <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" /> :
               user?.plan === 'starter' ? <Rocket className="w-5 h-5 text-blue-600 dark:text-blue-400" /> :
               <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Piano e Abbonamento</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Gestisci il tuo abbonamento e la fatturazione</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-sm font-bold uppercase ${
                  user?.plan === 'business' ? 'text-purple-600 dark:text-purple-400' :
                  user?.plan === 'pro' ? 'text-amber-600 dark:text-amber-400' :
                  user?.plan === 'starter' ? 'text-blue-600 dark:text-blue-400' :
                  'text-gray-600 dark:text-gray-400'
                }`}>
                  {isTrialUser
                    ? currentPlan === 'trial'
                      ? 'Trial Gratuito'
                      : `${currentPlanLabel} in prova`
                    : user?.plan || 'Nessun piano'}
                </span>
                {isTrialUser && user?.trial_days_left !== undefined && user.trial_days_left !== null && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    user.trial_days_left <= 3 ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400' :
                    'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400'
                  }`}>
                    {user.trial_days_left} giorni rimanenti
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isTrialUser 
                  ? `Durante il trial utilizzi i limiti del piano ${currentPlanLabel}`
                  : user?.plan === 'starter' ? 'Piano base per freelance e piccole attività'
                  : user?.plan === 'pro' ? 'Piano professionale con tutte le funzionalità'
                  : user?.plan === 'business' ? 'Piano enterprise per aziende e studi'
                  : 'Seleziona un piano per continuare'}
              </p>
              {user?.workspace_limit !== undefined && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {user.workspace_limit === -1 ? 'Cap workspace dedicato' : `${user.workspace_limit} workspace`}
                  {user?.transaction_limit && ` • ${user.transaction_limit.toLocaleString('it-IT')} transazioni/mese`}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {(isTrialUser || user?.plan === 'starter') && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="h-[44px] px-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 hover:-translate-y-px hover:shadow-btn-hover active:scale-[0.98] transition-all duration-200 ease-apple font-medium flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Upgrade
                </button>
              )}
              {user?.stripe_subscription_id && (
                <button
                  onClick={async () => {
                    setLoadingPortal(true)
                    try {
                      const response = await api.post('/stripe/portal')
                      window.open(response.data.url, '_blank')
                    } catch (err) {
                      console.error('Portal error:', err)
                      showToast('error', 'Errore nell\'apertura del portale di fatturazione')
                    } finally {
                      setLoadingPortal(false)
                    }
                  }}
                  disabled={loadingPortal}
                  className="px-4 py-2 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-all font-medium flex items-center gap-2"
                >
                  {loadingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                  Gestisci Abbonamento
                </button>
              )}
            </div>
          </div>
        </div>

        {isTrialUser && (
          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            Durante il trial usi i limiti del piano scelto. L'addebito parte solo dopo 14 giorni se non annulli.
          </p>
        )}
      </div>

      {/* Fiscal Data Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Dati Fiscali</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Configura i dati fiscali per personalizzare le scadenze</p>
            </div>
          </div>
          {fiscalSaved && (
            <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
              <Check className="w-4 h-4" /> Salvato
            </span>
          )}
        </div>

        {fiscalLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Dati Azienda */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ragione Sociale
                </label>
                <input
                  type="text"
                  value={fiscalData.ragione_sociale || ''}
                  onChange={(e) => handleFiscalChange('ragione_sociale', e.target.value)}
                  className="input w-full"
                  placeholder="Es. Mario Rossi SRL"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Partita IVA
                </label>
                <input
                  type="text"
                  value={fiscalData.partita_iva || ''}
                  onChange={(e) => handleFiscalChange('partita_iva', e.target.value)}
                  className="input w-full"
                  placeholder="Es. 12345678901"
                  maxLength={11}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Codice Fiscale
                </label>
                <input
                  type="text"
                  value={fiscalData.codice_fiscale || ''}
                  onChange={(e) => handleFiscalChange('codice_fiscale', e.target.value)}
                  className="input w-full"
                  placeholder="Es. RSSMRA80A01H501U"
                />
              </div>
            </div>

            {/* Regime e Tipo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Regime Fiscale
                </label>
                <select
                  value={fiscalData.regime_fiscale || ''}
                  onChange={(e) => handleFiscalChange('regime_fiscale', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Seleziona...</option>
                  <option value="ordinario">Ordinario</option>
                  <option value="semplificato">Semplificato</option>
                  <option value="forfettario">Forfettario</option>
                  <option value="minimi">Contribuenti Minimi</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo Attività
                </label>
                <select
                  value={fiscalData.tipo_attivita || ''}
                  onChange={(e) => handleFiscalChange('tipo_attivita', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Seleziona...</option>
                  <option value="ditta_individuale">Ditta Individuale</option>
                  <option value="professionista">Libero Professionista</option>
                  <option value="srl">S.R.L.</option>
                  <option value="srls">S.R.L.S.</option>
                  <option value="snc">S.N.C.</option>
                  <option value="sas">S.A.S.</option>
                  <option value="spa">S.P.A.</option>
                  <option value="altro">Altro</option>
                </select>
              </div>
            </div>

            {/* IVA */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Periodicità IVA
                </label>
                <select
                  value={fiscalData.periodicita_iva || ''}
                  onChange={(e) => handleFiscalChange('periodicita_iva', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Seleziona...</option>
                  <option value="mensile">Mensile</option>
                  <option value="trimestrale">Trimestrale</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Aliquota IVA Principale (%)
                </label>
                <input
                  type="number"
                  value={fiscalData.aliquota_iva_principale || ''}
                  onChange={(e) => handleFiscalChange('aliquota_iva_principale', parseFloat(e.target.value) || undefined)}
                  className="input w-full"
                  placeholder="22"
                  min={0}
                  max={100}
                />
              </div>
            </div>

            {/* Contributi */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cassa Previdenza
                </label>
                <select
                  value={fiscalData.cassa_previdenza || ''}
                  onChange={(e) => handleFiscalChange('cassa_previdenza', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Seleziona...</option>
                  <option value="inps_gestione_separata">INPS Gestione Separata</option>
                  <option value="inps_commercianti">INPS Commercianti</option>
                  <option value="inps_artigiani">INPS Artigiani</option>
                  <option value="cassa_professionale">Cassa Professionale</option>
                  <option value="nessuna">Nessuna</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  % Contributi
                </label>
                <input
                  type="number"
                  value={fiscalData.percentuale_contributi || ''}
                  onChange={(e) => handleFiscalChange('percentuale_contributi', parseFloat(e.target.value) || undefined)}
                  className="input w-full"
                  placeholder="26.23"
                  min={0}
                  max={100}
                  step={0.01}
                />
              </div>
            </div>

            {/* Ritenuta e Dipendenti */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={fiscalData.soggetto_ritenuta || false}
                    onChange={(e) => handleFiscalChange('soggetto_ritenuta', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Soggetto a Ritenuta d'Acconto</span>
                </label>
                {fiscalData.soggetto_ritenuta && (
                  <input
                    type="number"
                    value={fiscalData.percentuale_ritenuta || ''}
                    onChange={(e) => handleFiscalChange('percentuale_ritenuta', parseFloat(e.target.value) || undefined)}
                    className="input w-full"
                    placeholder="% Ritenuta (es. 20)"
                    min={0}
                    max={100}
                  />
                )}
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={fiscalData.ha_dipendenti || false}
                    onChange={(e) => handleFiscalChange('ha_dipendenti', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ha Dipendenti</span>
                </label>
                {fiscalData.ha_dipendenti && (
                  <input
                    type="number"
                    value={fiscalData.numero_dipendenti || ''}
                    onChange={(e) => handleFiscalChange('numero_dipendenti', parseInt(e.target.value) || undefined)}
                    className="input w-full"
                    placeholder="Numero dipendenti"
                    min={0}
                  />
                )}
              </div>
            </div>

            {/* Scadenze Custom */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Scadenze Personalizzate
                </label>
                <button
                  onClick={() => setShowAddDeadline(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Aggiungi
                </button>
              </div>

              {showAddDeadline && (
                <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg mb-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="date"
                      value={newDeadline.data}
                      onChange={(e) => setNewDeadline(prev => ({ ...prev, data: e.target.value }))}
                      className="input"
                    />
                    <input
                      type="text"
                      value={newDeadline.descrizione}
                      onChange={(e) => setNewDeadline(prev => ({ ...prev, descrizione: e.target.value }))}
                      placeholder="Descrizione scadenza"
                      className="input"
                    />
                    <input
                      type="number"
                      value={newDeadline.importo_stimato || ''}
                      onChange={(e) => setNewDeadline(prev => ({ ...prev, importo_stimato: parseFloat(e.target.value) || undefined }))}
                      placeholder="Importo stimato (€)"
                      className="input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={newDeadline.tipo}
                      onChange={(e) => setNewDeadline(prev => ({ ...prev, tipo: e.target.value }))}
                      className="input"
                    >
                      <option value="iva">IVA</option>
                      <option value="irpef">IRPEF</option>
                      <option value="inps">INPS</option>
                      <option value="inail">INAIL</option>
                      <option value="f24">F24</option>
                      <option value="altro">Altro</option>
                    </select>
                    <button onClick={addCustomDeadline} className="btn btn-primary">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setShowAddDeadline(false)} className="btn bg-gray-200 dark:bg-slate-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {fiscalData.scadenze_custom && fiscalData.scadenze_custom.length > 0 && (
                <div className="space-y-2">
                  {fiscalData.scadenze_custom.map((scad, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-slate-800 p-3 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium px-2 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded">
                          {scad.tipo.toUpperCase()}
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {new Date(scad.data).toLocaleDateString('it-IT')}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{scad.descrizione}</span>
                        {scad.importo_stimato && (
                          <span className="text-sm font-medium text-gray-900 dark:text-white">€{scad.importo_stimato.toLocaleString()}</span>
                        )}
                      </div>
                      <button onClick={() => removeCustomDeadline(index)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Note
              </label>
              <textarea
                value={fiscalData.note || ''}
                onChange={(e) => handleFiscalChange('note', e.target.value)}
                className="input w-full h-24"
                placeholder="Note aggiuntive sulla situazione fiscale..."
              />
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-slate-700">
              <button
                onClick={() => saveFiscalDataMutation.mutate(fiscalData)}
                disabled={saveFiscalDataMutation.isPending}
                className="btn btn-primary"
              >
                {saveFiscalDataMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Salva Dati Fiscali
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Team Section Header */}
      <div className="pt-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Users className="w-5 h-5" />
          Team
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">Gestisci i membri del team</p>
      </div>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <div className="card border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Inviti in sospeso
            </h3>
          </div>
          <div className="space-y-3">
            {invites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{invite.account_name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Invitato da {invite.inviter_email} come {invite.role === 'admin' ? 'Admin' : 'Membro'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => acceptInvite.mutate(invite.id)}
                    disabled={acceptInvite.isPending}
                    className="btn btn-primary btn-sm flex items-center gap-1"
                  >
                    <Check className="w-4 h-4" />
                    Accetta
                  </button>
                  <button
                    onClick={() => declineInvite.mutate(invite.id)}
                    disabled={declineInvite.isPending}
                    className="btn btn-secondary btn-sm flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Rifiuta
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Team - Create */}
      {accountData?.is_solo && (
        <div className="card">
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Non fai parte di nessun team
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              Crea un team per condividere i dati con altri utenti
            </p>

            {showCreateForm ? (
              <div className="max-w-sm mx-auto space-y-4">
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Nome del team..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg 
                    focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                />
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => createTeam.mutate(newTeamName)}
                    disabled={!newTeamName || createTeam.isPending}
                    className="btn btn-primary"
                  >
                    {createTeam.isPending ? 'Creazione...' : 'Crea Team'}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="btn btn-secondary"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCreateForm(true)}
                className="btn btn-primary"
              >
                <Users className="w-4 h-4 mr-2" />
                Crea Team
              </button>
            )}
          </div>
        </div>
      )}

      {/* Team Details */}
      {accountData?.account && (
        <>
          {/* Team Info */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary-100 rounded-lg">
                  <Users className="w-8 h-8 text-primary-600" />
                </div>
                <div>
                  {editingName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newTeamName}
                        onChange={(e) => setNewTeamName(e.target.value)}
                        className="px-3 py-1 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                      />
                      <button
                        onClick={() => updateTeamName.mutate(newTeamName)}
                        disabled={updateTeamName.isPending}
                        className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingName(false)}
                        className="p-1 text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {accountData.account.name}
                      </h2>
                      {(accountData.is_owner || accountData.my_role === 'admin') && (
                        <button
                          onClick={() => {
                            setNewTeamName(accountData.account!.name)
                            setEditingName(true)
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                  <p className="text-gray-500">
                    {accountData.account.member_count} {accountData.account.member_count === 1 ? 'membro' : 'membri'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadge(accountData.my_role)}`}>
                  {getRoleIcon(accountData.my_role)}
                  <span className="ml-1">
                    {accountData.my_role === 'owner' ? 'Proprietario' : 
                     accountData.my_role === 'admin' ? 'Admin' : 'Membro'}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Members */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="card-title">Membri del Team</h3>
              <div className="flex gap-2">
                {(accountData.is_owner || accountData.my_role === 'admin') && sentInvitesData && sentInvitesData.invites.length > 0 && (
                  <button
                    onClick={() => setShowSentInvites(!showSentInvites)}
                    className="btn btn-secondary btn-sm"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Inviti ({sentInvitesData.invites.filter(i => i.status === 'pending').length})
                  </button>
                )}
                {(accountData.is_owner || accountData.my_role === 'admin') && (
                  <button
                    onClick={() => setShowInviteForm(!showInviteForm)}
                    className="btn btn-primary btn-sm"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Invita
                  </button>
                )}
              </div>
            </div>

            {/* Sent Invites Section */}
            {showSentInvites && sentInvitesData && sentInvitesData.invites.length > 0 && (
              <div className="mb-4 p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Inviti Mandati</h4>
                <div className="space-y-2">
                  {sentInvitesData.invites.map((invite) => (
                    <div key={invite.id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        {invite.status === 'pending' && <Clock className="w-4 h-4 text-amber-500" />}
                        {invite.status === 'accepted' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                        {invite.status === 'declined' && <XCircle className="w-4 h-4 text-red-500" />}
                        {invite.status === 'expired' && <Clock className="w-4 h-4 text-gray-400" />}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{invite.invitee_email}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {invite.role === 'admin' ? 'Admin' : 'Membro'} • 
                            {new Date(invite.created_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          invite.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400' :
                          invite.status === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400' :
                          invite.status === 'declined' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400' :
                          'bg-gray-100 text-gray-700 dark:bg-gray-900/50 dark:text-gray-400'
                        }`}>
                          {invite.status === 'pending' ? 'In attesa' :
                           invite.status === 'accepted' ? 'Accettato' :
                           invite.status === 'declined' ? 'Rifiutato' : 'Scaduto'}
                        </span>
                        {invite.status === 'pending' && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Vuoi revocare l'invito a ${invite.invitee_email}?`)) {
                                revokeInvite.mutate(invite.id)
                              }
                            }}
                            disabled={revokeInvite.isPending}
                            className="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Revoca invito"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invite Form */}
            {showInviteForm && (
              <div className="mb-4 p-4 bg-gray-50 dark:bg-slate-700 rounded-lg space-y-3">
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Email del nuovo membro..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg 
                      focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                    className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  >
                    <option value="member">Membro</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                {/* Workspace Selection */}
                {workspacesData && workspacesData.workspaces.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Accesso ai Workspace (vuoto = tutti)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {workspacesData.workspaces.map((ws) => (
                        <label key={ws.id} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700">
                          <input
                            type="checkbox"
                            checked={inviteWorkspaceIds.includes(ws.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setInviteWorkspaceIds([...inviteWorkspaceIds, ws.id])
                              } else {
                                setInviteWorkspaceIds(inviteWorkspaceIds.filter(id => id !== ws.id))
                              }
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-900 dark:text-white">{ws.name}</span>
                        </label>
                      ))}
                    </div>
                    {inviteWorkspaceIds.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">L'utente avrà accesso a tutti i workspace</p>
                    )}
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={() => inviteUser.mutate({ 
                      email: inviteEmail, 
                      role: inviteRole,
                      workspace_ids: inviteWorkspaceIds.length > 0 ? inviteWorkspaceIds : undefined
                    })}
                    disabled={!inviteEmail || inviteUser.isPending}
                    className="btn btn-primary"
                  >
                    {inviteUser.isPending ? 'Invio...' : 'Invia Invito'}
                  </button>
                </div>
                {inviteUser.isSuccess && (
                  <p className="text-sm text-green-600">Invito inviato!</p>
                )}
                {inviteUser.isError && (
                  <p className="text-sm text-red-600">
                    {(inviteUser.error as Error & { response?: { data?: { error?: string } } })?.response?.data?.error || 'Errore nell\'invio'}
                  </p>
                )}
              </div>
            )}

            {/* Members List */}
            <div className="divide-y divide-gray-100 dark:divide-slate-700">
              {accountData.account.members.map((member) => (
                <div key={member.user_id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 dark:bg-slate-700 rounded-full">
                      {getRoleIcon(member.role)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{member.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{member.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadge(member.role)}`}>
                      {member.role === 'owner' ? 'Proprietario' : 
                       member.role === 'admin' ? 'Admin' : 'Membro'}
                    </span>

                    {/* Role change dropdown - only for owner */}
                    {accountData.is_owner && member.role !== 'owner' && (
                      <select
                        value={member.role}
                        onChange={(e) => updateMemberRole.mutate({ 
                          email: member.email, 
                          role: e.target.value as 'admin' | 'member' 
                        })}
                        className="text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                      >
                        <option value="member">Membro</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}

                    {/* Remove member - for owner/admin, not self, not owner */}
                    {(accountData.is_owner || accountData.my_role === 'admin') && 
                     member.role !== 'owner' && (
                      <button
                        onClick={async () => {
                          const confirmed = await confirm({
                            title: 'Rimuovi membro',
                            message: `Sei sicuro di voler rimuovere ${member.name} dal team?`,
                            confirmText: 'Rimuovi',
                            type: 'danger'
                          })
                          if (confirmed) {
                            removeMember.mutate(member.email)
                          }
                        }}
                        className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        title="Rimuovi membro"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Leave Team - only for non-owners */}
          {!accountData.is_owner && (
            <div className="card border border-red-200 dark:border-red-800">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">Abbandona Team</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    I tuoi dati personali non saranno più condivisi con il team
                  </p>
                </div>
                <button
                  onClick={async () => {
                    const confirmed = await confirm({
                      title: 'Abbandona Team',
                      message: 'Sei sicuro di voler abbandonare il team? I tuoi dati personali non saranno più condivisi.',
                      confirmText: 'Abbandona',
                      type: 'danger'
                    })
                    if (confirmed) {
                      leaveAccount.mutate()
                    }
                  }}
                  className="btn bg-red-500 text-white hover:bg-red-600"
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  Abbandona
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false)
          refreshUser()
        }}
        trialExpired={user?.is_trial_expired}
        trialDaysLeft={user?.trial_days_left}
        currentPlan={user?.plan}
      />
    </div>
  )
}
